import { Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WidgetValidator } from '@pt/pt-models';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ValidatorGenerator } from '../../utils';

@Component({
  selector: 'pt-input-select',
  templateUrl: './input-select.component.html',
  styles: [`
    .invalid-custom {
      border: 1px solid #dc3545;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputSelectComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputSelectComponent),
      multi: true
    }
  ]
})
export class InputSelectComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {

  @Input() controlName: string;
  @Input() validators: WidgetValidator[];
  @Input() label: string;
  @Input() fontWeightBold: boolean;
  @Input() placeholder: string;
  @Input() help: string;

  @Input() labelHelp?: string;
  @Input() labelHelpType: 'DIALOG' | 'TOOLTIP' = 'TOOLTIP';

  @Input() error: string;    // Da implementare
  @Input() options: InputSelectData[];
  @Input() clearable = true;
  @Input() searchable = true;
  @Input() useLabelReadOnly = false;
  @Input() showInputLabelReadOnly = false;
  @Input() notFoundText: string;
  @Input() cssClass?: string

  public formGroup: FormGroup;
  isRequired: boolean;
  inputId: string;
  isChanged = false;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private onChanged: (val) => void;
  private onTouched: () => void;
  private isDisabled = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.validators) {
      this.setFormGroup(true);
    } else {
      this.setFormGroup(false);
    }

    if (changes.options) {
      this.setFormGroup(false);
    }
  }


  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.inputId = 'ptWSelect_' + this.controlName;
    this.isRequired = ValidatorGenerator.validatorsContainsRequired(this.validators);
  }

  writeValue(obj: any): void {
    this.formGroup.patchValue({value: obj});
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.formGroup.controls.value.disable({emitEvent: false});
    } else {
      this.formGroup.controls.value.enable({emitEvent: false});
    }
  }

  validate(value: FormControl) {
    return this.formGroup.invalid && {
      invalid: true
    };
  }

  findOptionBorderColorCss() {
    return this.options?.find(o => o.value === this.formGroup?.controls?.value?.value)?.borderColorCss;
  }

  findOptionCssClass() {
    return this.options?.find(o => o.value === this.formGroup?.controls?.value?.value)?.cssClass;
  }

  findOptionLabel() {
    return this.options?.find(o => o.value === this.formGroup?.controls?.value?.value)?.label;
  }

  private setFormGroup(changeValidator?: boolean) {
    this.destroy$.next(true);

    const validator = ValidatorGenerator.generateValidator(this.validators);

    const value = this.formGroup ? this.formGroup.controls.value.value : null;
    this.formGroup = new FormGroup({
      value: new FormControl(value, validator)
    });

    if (this.isDisabled) {
      this.formGroup.disable({emitEvent: false});
    }

    this.formGroup.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(t => {
      this.isChanged = t.value !== null;
      if (this.onChanged) {
        this.onChanged(t.value);
      }
      if (this.onTouched) {
        this.onTouched();
      }
    });

    if (changeValidator) {
      this.isChanged = true;
      if (this.onChanged) {
        this.onChanged(this.formGroup.controls.value.value);
      }
      if (this.onTouched) {
        this.onTouched();
      }
    }
  }
}

export interface InputSelectData {
  label: Observable<string>,
  value: unknown,
  disabled?: boolean,
  cssClass?: string,
  borderColorCss?: string,
}
